import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Clock, TrendingUp, Award, Video, FileText, Target, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { usePrepAuth } from '../../contexts/PrepAuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = { video: Video, pdf: FileText, practice_test: Target, interactive: BookOpen };
const TYPE_COLOR = { video: 'text-blue-600 bg-blue-50', pdf: 'text-green-600 bg-green-50', practice_test: 'text-purple-600 bg-purple-50', interactive: 'text-orange-600 bg-orange-50' };

export const PrepDashboard = () => {
  const { user } = usePrepAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState({});
  const [materials, setMaterials] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const enrollRes = await axios.get(`${API}/prep/enrollments`, { withCredentials: true });
      const activeEnrollments = enrollRes.data.filter(e => e.status === 'active');
      setEnrollments(activeEnrollments);

      const programMap = {};
      for (const e of activeEnrollments) {
        try {
          const pRes = await axios.get(`${API}/prep/programs/${e.program_id}`);
          programMap[e.program_id] = pRes.data;
        } catch {}
      }
      setPrograms(programMap);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadMaterials = async (programId) => {
    if (materials[programId]) return;
    try {
      const r = await axios.get(`${API}/prep/programs/${programId}/materials`, { withCredentials: true });
      setMaterials(prev => ({ ...prev, [programId]: r.data }));
    } catch (e) {
      console.error(e);
      setMaterials(prev => ({ ...prev, [programId]: [] }));
    }
  };

  const toggleExpand = (programId) => {
    const next = !expanded[programId];
    setExpanded(prev => ({ ...prev, [programId]: next }));
    if (next) loadMaterials(programId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Welcome back, {user?.name || 'Student'}!
          </h1>
          <p className="text-blue-100 text-lg">Continue your learning journey</p>
        </div>
      </section>

      <section className="-mt-8 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { label: 'Enrolled Programs', value: enrollments.length, icon: BookOpen, color: 'text-blue-600' },
              { label: 'Active Progress', value: `${enrollments.length > 0 ? Math.round(enrollments.reduce((s, e) => s + (e.progress || 0), 0) / enrollments.length) : 0}%`, icon: TrendingUp, color: 'text-purple-600' },
              { label: 'Status', value: enrollments.length > 0 ? 'Active' : 'No Enrollments', icon: Award, color: 'text-green-600' },
            ].map(card => {
              const Icon = card.icon;
              return (
                <Card key={card.label} className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium text-gray-600">{card.label}</CardTitle>
                      <Icon className={card.color} size={20} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold" data-testid={`stat-${card.label.toLowerCase().replace(/ /g, '-')}`}>{card.value}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">My Programs</h2>
              {loading && <div className="text-center py-8 text-gray-500">Loading your programs...</div>}
              {!loading && enrollments.length === 0 && (
                <Card className="border-2">
                  <CardContent className="py-12 text-center">
                    <BookOpen className="mx-auto text-gray-300 mb-4" size={48} />
                    <p className="text-gray-600 mb-4">You haven't enrolled in any programs yet.</p>
                    <Link to="/prep/programs">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="browse-programs-btn">
                        Browse Programs
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
              {enrollments.map((enrollment) => {
                const program = programs[enrollment.program_id];
                const programMaterials = materials[enrollment.program_id];
                const isExpanded = expanded[enrollment.program_id];
                return (
                  <Card key={enrollment.id} className="border-2 hover:shadow-lg transition-all" data-testid={`enrollment-card-${enrollment.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-xl">{program?.title || 'Loading...'}</CardTitle>
                          <CardDescription className="mt-1">
                            Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()}
                          </CardDescription>
                        </div>
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Overall Progress</span>
                            <span className="font-semibold">{enrollment.progress}%</span>
                          </div>
                          <Progress value={enrollment.progress} className="h-3" />
                        </div>
                        <div className="flex gap-3">
                          <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid={`continue-learning-${enrollment.id}`}>
                            Continue Learning
                          </Button>
                          <Button
                            variant="outline"
                            className="border-blue-600 text-blue-600"
                            onClick={() => toggleExpand(enrollment.program_id)}
                            data-testid={`view-materials-${enrollment.id}`}
                          >
                            {isExpanded ? <ChevronUp size={16} className="mr-1" /> : <ChevronDown size={16} className="mr-1" />}
                            Materials
                          </Button>
                        </div>

                        {isExpanded && (
                          <div className="border-t pt-4">
                            <h4 className="font-semibold text-gray-900 mb-3">Course Materials</h4>
                            {!programMaterials && <p className="text-sm text-gray-500">Loading...</p>}
                            {programMaterials && programMaterials.length === 0 && (
                              <p className="text-sm text-gray-500">No materials available yet.</p>
                            )}
                            {programMaterials && programMaterials.map((mat) => {
                              const Icon = TYPE_ICON[mat.type] || FileText;
                              const colorClass = TYPE_COLOR[mat.type] || 'text-gray-600 bg-gray-50';
                              return (
                                <a
                                  key={mat.id}
                                  href={mat.content_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 border mb-2 transition-colors group"
                                  data-testid={`material-link-${mat.id}`}
                                >
                                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass.split(' ').slice(1).join(' ')}`}>
                                    <Icon className={colorClass.split(' ')[0]} size={18} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 text-sm">{mat.title}</p>
                                    <p className="text-xs text-gray-500 capitalize">{mat.type.replace('_', ' ')}</p>
                                  </div>
                                  <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { to: '/prep/programs', label: 'Browse Programs', desc: 'Explore new certification programs', icon: Video, bg: 'bg-blue-100', color: 'text-blue-600' },
                    { to: '/prep/individual', label: 'Individual Materials', desc: 'Buy specific study resources', icon: FileText, bg: 'bg-purple-100', color: 'text-purple-600' },
                  ].map(item => {
                    const Icon = item.icon;
                    return (
                      <Link key={item.to} to={item.to}>
                        <Card className="border-2 hover:shadow-lg transition-all cursor-pointer h-full">
                          <CardHeader>
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 ${item.bg} rounded-lg flex items-center justify-center`}>
                                <Icon className={item.color} size={20} />
                              </div>
                              <CardTitle className="text-lg">{item.label}</CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <CardDescription>{item.desc}</CardDescription>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <Card className="border-2 bg-gradient-to-br from-blue-50 to-blue-100">
                <CardHeader>
                  <CardTitle>Explore More Programs</CardTitle>
                  <CardDescription>Expand your knowledge with additional certifications</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to="/prep/programs">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">Browse All Programs</Button>
                  </Link>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="text-blue-600" size={20} />
                    <CardTitle>Schedule a Session</CardTitle>
                  </div>
                  <CardDescription>Book one-on-one tutoring time with an instructor</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600">
                    Book Session
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="py-6">
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="font-semibold text-gray-900 mb-2">Account Info</p>
                    <p>{user?.name}</p>
                    <p className="text-gray-500">{user?.email}</p>
                    <Badge className="mt-2 bg-blue-100 text-blue-700 capitalize">{user?.role || 'student'}</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
